import React, { createRef, RefObject, useState } from 'react';
import { isNil } from 'lodash';
import { classes } from 'typestyle';

import { Fab, Button, Tooltip } from '@material-ui/core';
import SquareActionButton from 'src/common-ui/components/Buttons/SquareActionButton';
import { fabBtn } from 'src/pages/AssortmentStrategy/TargetSetting/TargetList/TargetList.styles';
import ButtonModal from 'src/components/ButtonModal/ButtonModal';
import { TEAL_PRIMARY } from 'src/common-ui/theme';
import {
  ConfigApi,
  ClientDataApi,
  DataApi,
  ListDataApi,
  isListDataApi,
} from 'configuration-schemas/confdefnView';
import AddUserModal from '../AddUserModal/AddUserModal';
import { AppState } from 'src/store';
import Axios from 'src/services/axios';
import { toast } from 'react-toastify';
import ServiceContainer from 'src/ServiceContainer';
import { toastAndLog } from 'src/services/loggingService';
import store from 'src/store';
import { requestRefreshAfterPlan } from 'src/pages/AssortmentBuild/Planning.slice';

export enum FabType {
  none = 'none',
  buttonModal = 'buttonModal',
  worklist = 'worklist',
  cart = 'cart',
  planning = 'planning',
  postTextModal = 'postTextModal',
  pivot = 'pivot',
}

export interface FabClickHandlerOptions {
  pivotFabDataApi?: DataApi;
  defnId?: string;
}

export interface FabHandlerInvocable {
  onFabClick: (options?: FabClickHandlerOptions) => Promise<void>;
  onToggleAll?: () => void;
}

export interface FabProps {
  fabType: FabType;
  fabTooltip?: string;
  configApi?: ConfigApi;
  text?: string;
  dataApi?: ClientDataApi | ListDataApi;
  fabIcon?: string;
  defnId?: string;
  showRefreshFab?: boolean;
  toastError?: boolean;
}
// these are all the expected property keys that help drive the logic of the fabTypes
// duplicate loading props is because different components used different loading props
export interface WithFabProps {
  fab: FabProps;
  isPrintMode: boolean;
  loaded: boolean;
  isLoading: boolean;
  cartCount: number; // needed because fab cart btn renders number of items in cart
  showSelectAll: boolean;
  isSelectAllDisabled: boolean;
}

export function getfabProps(state: AppState, fabType: string): FabProps {
  if (state.appConfig.tenantConfig.fabTypes) {
    return state.appConfig.tenantConfig.fabTypes[fabType] as FabProps;
  } else {
    return { fabType: FabType.none };
  }
}

function getFabContent(
  wrappedComponentRef: RefObject<FabHandlerInvocable>,
  fabContentProps: Pick<WithFabProps, 'cartCount' | 'showSelectAll' | 'isSelectAllDisabled'>,
  fab: FabProps,
  loading: boolean,
  setLoading: (loading: boolean) => void, // Pass setLoading to control the loading state
  showRefresh: boolean,
  setShowRefresh: (showRefresh: boolean) => void,
) {
  if (!isNil(fab)) {
    const { fabType, fabTooltip, configApi, dataApi, text, fabIcon, defnId } = fab;
    switch (fabType) {
      case FabType.buttonModal:
        return <ButtonModal tooltipString={fabTooltip} defn={configApi?.params.defnId} />;
      case FabType.worklist:
        return (
          <section className={fabBtn}>
            <Fab
              color="secondary"
              aria-label="Add visible items to worklist"
              onClick={() => {
                if (wrappedComponentRef && wrappedComponentRef.current) {
                  wrappedComponentRef.current.onFabClick();
                }
              }}
            >
              <i style={{ color: 'white' }} className={classes('far fa-lg fa-plus')} />
            </Fab>
          </section>
        );
      case FabType.cart:
        const { cartCount, showSelectAll, isSelectAllDisabled } = fabContentProps;
        const isFabDisabled = cartCount <= 0;
        return (
          <React.Fragment>
            <section
              className={fabBtn}
              style={{
                bottom: 15,
                right: 35,
              }}
            >
              <SquareActionButton
                ariaLabel='Create New Target'
                text={`Add to Cart (${cartCount})`}
                onClick={() => {
                  if (wrappedComponentRef && wrappedComponentRef.current) {
                    wrappedComponentRef.current.onFabClick();
                  }
                }}
                disabled={isFabDisabled}
              />
            </section>
            {showSelectAll && (
              <section
                className={fabBtn}
                style={{
                  backgroundColor: isSelectAllDisabled ? 'rgb(174, 174, 174)' : TEAL_PRIMARY,
                  bottom: '5px',
                  right: 185,
                }}
              >
                <Button
                  style={{
                    color: isSelectAllDisabled ? 'rgb(244, 244, 244)' : 'white',
                  }}
                  onClick={() => {
                    if (wrappedComponentRef && wrappedComponentRef.current && wrappedComponentRef.current.onToggleAll) {
                      wrappedComponentRef.current.onToggleAll();
                    }
                  }}
                  disabled={isSelectAllDisabled}
                >
                  Select All
                </Button>
              </section>
            )}
          </React.Fragment>
        );

      case FabType.planning:
        return (
          <section className={fabBtn}>
            <Tooltip title={fabTooltip || 'Replan pending items'} enterDelay={0} arrow>
              <Fab
                color="secondary"
                aria-label="Update Plan"
                onClick={async () => {
                  // block another click while loading
                  if (wrappedComponentRef && wrappedComponentRef.current && !loading) {
                    setLoading(true);
                    try {
                      await wrappedComponentRef.current.onFabClick({ defnId: !isNil(defnId) ? defnId : '' });
                    } catch (e) {
                      setLoading(false);
                      toastAndLog('An error occurred during planning', e)
                    } finally {
                      // This action happens fast, need a timeout to make sure a brief loading period displays
                      setTimeout(() => {
                        setLoading(false);
                      }, 1000);
                    }
                  }
                }}
              >
                {loading ? (
                  <i style={{ color: 'white' }} className="fa fa-spinner fa-spin" />
                ) : (
                  <i style={{ color: 'white' }} className={classes('far fa-lg fa-paper-plane')} />
                )}
              </Fab>
            </Tooltip>
          </section>
        );
      case FabType.postTextModal:
        if (isListDataApi(dataApi) || isNil(dataApi)) return;
        return (
          <AddUserModal
            text={text || 'Email address of the user to be added:'}
            fabTooltip={fabTooltip}
            dataApi={dataApi}
          />
        );
      case FabType.pivot:
        return !showRefresh ? (
          <section className={fabBtn}>
            <Tooltip title={fabTooltip || 'Call configured pivot'} enterDelay={0} arrow>
              <Fab
                color="secondary"
                aria-label="Call configured pivot"
                onClick={async () => {
                  if (isNil(dataApi)) return;
                  setLoading(true);
                  if (!isListDataApi(dataApi)) {
                    setLoading(true);
                    const methodType = dataApi.method ?? 'get';
                    Axios[methodType](dataApi.url, {
                      params: dataApi.params,
                    })
                      .catch(() => {
                        if (fab.toastError) {
                          toast(<div>An error occurred during run of the configured pivot.</div>, {
                            position: toast.POSITION.TOP_RIGHT,
                            type: 'error',
                          });
                        }
                      })
                      .finally(() => {
                        if (fab.showRefreshFab) {
                          setShowRefresh(true);
                        }
                        setLoading(false); // Reset loading state after the request is complete
                      });
                  } else if (wrappedComponentRef?.current?.onFabClick) {
                    await wrappedComponentRef.current.onFabClick({ pivotFabDataApi: dataApi }).finally(() => {
                      setLoading(false);
                      if (fab.showRefreshFab) {
                        setShowRefresh(true);
                      }
                    });
                  } else if (isListDataApi(dataApi)) {
                    return ServiceContainer.pivotService
                      .listData(dataApi.defnId, undefined, dataApi.params)
                      .catch((error) => {
                        // @ts-ignore
                        if (fab.toastError) {
                          toastAndLog('An error occurred during pivot processing', error);
                        }
                      })
                      .finally(() => {
                        setLoading(false);
                        if (fab.showRefreshFab) {
                          setShowRefresh(true);
                        }
                      });
                  }
                }}
              >
                {loading ? (
                  <i style={{ color: 'white' }} className="fa fa-spinner fa-spin" />
                ) : (
                  <i style={{ color: 'white' }} className={classes(fabIcon)} />
                )}
              </Fab>
            </Tooltip>
          </section>
        ) : (
          <section className={fabBtn}>
            <Tooltip title={'Refresh data'} enterDelay={0} arrow>
              <Fab
                color="secondary"
                aria-label="Refresh data"
                onClick={async () => {
                  // don't try this at home
                  store.dispatch(requestRefreshAfterPlan()); // most views trigger refresh when this happens.  This might put the plan queue in a bad state
                  setShowRefresh(false);
                }}
              >
                <i style={{ color: 'white' }} className="far fa-sync-alt" />
              </Fab>
            </Tooltip>
          </section>
        );
      case FabType.none:
      default:
        return undefined;
    }
  } else return undefined;
}

export function withFab<P>(WrappedComponent: React.ComponentType<P>) {
  const FabWrappedComponent = (props: P & WithFabProps) => {
    const { isPrintMode, loaded, isLoading: viewLoading, fab, ...fabContentProps } = props;
    const wrappedRef: RefObject<FabHandlerInvocable> = createRef();
    const [loading, setLoading] = useState(false); // Add loading state

    /** `showRefesh` here is an optional boolean property only for fab pivots,
     * that can be set to true in the fab config.  When `true`, once the pivot complets the fab will turn into a refresh icon
     */
    const [showRefresh, setShowRefresh] = useState(false);

    // consolidate loading variables from different views into single value
    const isLoading = (!isNil(loaded) && !loaded) || (!isNil(viewLoading) && viewLoading);
    if (isPrintMode || isLoading) {
      return <WrappedComponent {...props} />;
    }
    let fabContent = getFabContent(wrappedRef, fabContentProps, fab, loading, setLoading, showRefresh, setShowRefresh);
    if (fab?.fabTooltip && (fab?.fabType == FabType.worklist || fab?.fabType == FabType.cart)) {
      fabContent = (
        <Tooltip title={fab?.fabTooltip} enterDelay={0} arrow>
          {fabContent || <div />}
        </Tooltip>
      );
    }
    return (
      <React.Fragment>
        <WrappedComponent ref={wrappedRef} {...props} />
        {fabContent}
      </React.Fragment>
    );
  };

  FabWrappedComponent.displayName = 'FabWrappedComponent';
  return FabWrappedComponent;
}
